/**
 * Offline-first storage + sync for the workout data.
 *
 * The UI (public/app.js) works with one plain object: the in-progress workout
 * fields plus `history`, the list of finished workouts. This store keeps that
 * object in localStorage so the app works with no signal, and syncs it to the
 * account in the background:
 *
 *   - `history` is split into individual workouts and pushed as upserts and
 *     deletes (only the ones that changed).
 *   - everything else is the "active state", pushed as one document with a
 *     revision number. If another device saved a newer revision first, the
 *     server refuses ours and we adopt theirs.
 *
 * Local changes always win over incoming ones until they have been pushed.
 */
import type {
  ActiveState,
  PushActiveConflict,
  PushActiveOk,
  PushWorkoutsRequest,
  SyncPullResponse,
  Workout,
} from "@wreps/shared";
import { ApiHttpError, OfflineError } from "./auth.js";

export type SyncStatus = "synced" | "pending" | "syncing" | "offline" | "error";

export interface AppData extends Record<string, unknown> {
  history: Workout[];
}

export interface SyncApi {
  fetchJson<T>(path: string, init?: RequestInit & { json?: unknown }): Promise<T>;
}

export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface CacheShape {
  v: 1;
  cursor: number;
  /** Has this device completed at least one pull? */
  pulledOnce: boolean;
  state: ActiveState | null;
  stateRevision: number;
  stateDirty: boolean;
  workouts: Record<string, Workout>;
  dirtyWorkouts: string[];
  deletedWorkouts: string[];
}

const PUSH_BATCH = 100;

function emptyCache(): CacheShape {
  return {
    v: 1,
    cursor: 0,
    pulledOnce: false,
    state: null,
    stateRevision: 0,
    stateDirty: false,
    workouts: {},
    dirtyWorkouts: [],
    deletedWorkouts: [],
  };
}

export function cacheKey(userId: string): string {
  return `wreps:data:v1:${userId}`;
}

function sortHistory(list: Workout[]): Workout[] {
  return list.sort((a, b) =>
    a.date === b.date ? a.id.localeCompare(b.id) : a.date < b.date ? -1 : 1,
  );
}

export class SyncStore {
  private cache: CacheShape;
  private dirty: Set<string>;
  private deleted: Set<string>;
  private stateJson: string;
  private status: SyncStatus = "synced";
  private running: Promise<void> | null = null;
  private again = false;
  private pushTimer: ReturnType<typeof setTimeout> | null = null;
  private remoteListeners = new Set<(data: AppData, reason: "pull" | "conflict") => void>();
  private statusListeners = new Set<(s: SyncStatus) => void>();
  lastSyncedAt: number | null = null;

  constructor(
    private readonly userId: string,
    private readonly api: SyncApi,
    private readonly storage: KeyValueStorage = localStorage,
    private readonly debounceMs = 800,
  ) {
    this.cache = this.readCache();
    this.dirty = new Set(this.cache.dirtyWorkouts);
    this.deleted = new Set(this.cache.deletedWorkouts);
    this.stateJson = JSON.stringify(this.cache.state ?? null);
    this.status = this.hasPending() ? "pending" : "synced";
  }

  // ---------- local cache ----------

  private readCache(): CacheShape {
    try {
      const raw = this.storage.getItem(cacheKey(this.userId));
      if (raw) {
        const parsed = JSON.parse(raw) as CacheShape;
        if (parsed && parsed.v === 1) return { ...emptyCache(), ...parsed };
      }
    } catch {
      // corrupt or unavailable: start clean, the server has the data
    }
    return emptyCache();
  }

  private writeCache(): void {
    this.cache.dirtyWorkouts = [...this.dirty];
    this.cache.deletedWorkouts = [...this.deleted];
    try {
      this.storage.setItem(cacheKey(this.userId), JSON.stringify(this.cache));
    } catch (e) {
      console.warn("wreps: could not write local cache", e);
    }
  }

  /** Remove this user's data from the device (sign-out). */
  clearLocal(): void {
    this.stop();
    try {
      this.storage.removeItem(cacheKey(this.userId));
    } catch {}
  }

  // ---------- what the app sees ----------

  /** The app's data object, or null if this device has never loaded any. */
  snapshot(): AppData | null {
    const history = sortHistory(Object.values(this.cache.workouts).map((w) => structuredClone(w)));
    if (!this.cache.state && history.length === 0 && !this.cache.pulledOnce) return null;
    return { ...(structuredClone(this.cache.state) ?? {}), history };
  }

  /**
   * For the app's `loadData()`. On a device that has never synced, waits
   * briefly for the first pull so the user doesn't start a workout on top of
   * an empty screen while their data is on its way.
   */
  async load(firstPullTimeoutMs = 6000): Promise<AppData | null> {
    if (!this.cache.pulledOnce) {
      await Promise.race([
        this.syncNow(),
        new Promise((resolve) => setTimeout(resolve, firstPullTimeoutMs)),
      ]);
    } else {
      void this.syncNow();
    }
    return this.snapshot();
  }

  /** For the app's `saveData()`: record local changes and schedule a push. */
  save(data: AppData): void {
    const { history, ...state } = data;
    let changed = false;

    const nextStateJson = JSON.stringify(state);
    if (nextStateJson !== this.stateJson) {
      this.cache.state = JSON.parse(nextStateJson) as ActiveState;
      this.stateJson = nextStateJson;
      this.cache.stateDirty = true;
      changed = true;
    }

    const seen = new Set<string>();
    for (const w of Array.isArray(history) ? history : []) {
      if (!w || typeof w.id !== "string") continue;
      seen.add(w.id);
      const prev = this.cache.workouts[w.id];
      const json = JSON.stringify(w);
      if (!prev || JSON.stringify(prev) !== json) {
        this.cache.workouts[w.id] = JSON.parse(json) as Workout;
        this.dirty.add(w.id);
        this.deleted.delete(w.id);
        changed = true;
      }
    }
    for (const id of Object.keys(this.cache.workouts)) {
      if (!seen.has(id)) {
        delete this.cache.workouts[id];
        this.dirty.delete(id);
        this.deleted.add(id);
        changed = true;
      }
    }

    if (!changed) return;
    this.writeCache();
    this.setStatus("pending");
    this.schedulePush();
  }

  hasPending(): boolean {
    return this.cache.stateDirty || this.dirty.size > 0 || this.deleted.size > 0;
  }

  pendingCount(): number {
    return (this.cache.stateDirty ? 1 : 0) + this.dirty.size + this.deleted.size;
  }

  getStatus(): SyncStatus {
    return this.status;
  }

  onRemoteChange(fn: (data: AppData, reason: "pull" | "conflict") => void): () => void {
    this.remoteListeners.add(fn);
    return () => this.remoteListeners.delete(fn);
  }

  onStatus(fn: (s: SyncStatus) => void): () => void {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  private setStatus(s: SyncStatus): void {
    if (s === this.status) return;
    this.status = s;
    for (const fn of this.statusListeners) fn(s);
  }

  private emitRemote(reason: "pull" | "conflict"): void {
    const snap = this.snapshot();
    if (!snap) return;
    for (const fn of this.remoteListeners) fn(snap, reason);
  }

  // ---------- syncing ----------

  private schedulePush(): void {
    if (this.pushTimer) clearTimeout(this.pushTimer);
    this.pushTimer = setTimeout(() => {
      this.pushTimer = null;
      void this.syncNow();
    }, this.debounceMs);
  }

  stop(): void {
    if (this.pushTimer) clearTimeout(this.pushTimer);
    this.pushTimer = null;
  }

  /** Push local changes, then pull remote ones. Calls made while running coalesce. */
  syncNow(): Promise<void> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = (async () => {
      try {
        do {
          this.again = false;
          this.setStatus("syncing");
          await this.pushWorkouts();
          await this.pushState();
          await this.pull();
        } while (this.again);
        this.lastSyncedAt = Date.now();
        this.setStatus(this.hasPending() ? "pending" : "synced");
      } catch (e) {
        if (e instanceof OfflineError) this.setStatus("offline");
        else {
          console.warn("wreps: sync failed", e);
          this.setStatus("error");
        }
        // Retry later; local data is safe in the cache meanwhile.
        if (this.hasPending()) this.retryLater();
      } finally {
        this.running = null;
      }
    })();
    return this.running;
  }

  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryLater(): void {
    if (this.retryTimer) return;
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      void this.syncNow();
    }, 15_000);
  }

  private async pushWorkouts(): Promise<void> {
    while (this.dirty.size > 0 || this.deleted.size > 0) {
      const upIds = [...this.dirty].slice(0, PUSH_BATCH);
      const delIds = [...this.deleted].slice(0, PUSH_BATCH);
      const upserts = upIds
        .map((id) => this.cache.workouts[id])
        .filter((w): w is Workout => Boolean(w));
      const sent = new Map(upserts.map((w) => [w.id, JSON.stringify(w)]));
      const body: PushWorkoutsRequest = { upserts, deletes: delIds };
      await this.api.fetchJson("/v1/sync/workouts", { method: "POST", json: body });
      // Only clear what didn't change again while the request was in flight.
      for (const id of upIds) {
        const now = this.cache.workouts[id];
        if (!now || JSON.stringify(now) === sent.get(id)) this.dirty.delete(id);
      }
      for (const id of delIds) if (!this.cache.workouts[id]) this.deleted.delete(id);
      this.writeCache();
    }
  }

  private async pushState(): Promise<void> {
    if (!this.cache.stateDirty) return;
    const sentJson = this.stateJson;
    try {
      const ok = await this.api.fetchJson<PushActiveOk>("/v1/sync/active", {
        method: "PUT",
        json: { baseRevision: this.cache.stateRevision, state: this.cache.state ?? {} },
      });
      this.cache.stateRevision = ok.revision;
      if (this.stateJson === sentJson) this.cache.stateDirty = false;
      this.writeCache();
    } catch (e) {
      if (e instanceof ApiHttpError && e.status === 409) {
        // Another device moved the workout on first: theirs wins.
        const { active } = e.body as PushActiveConflict;
        this.adoptState(active.state, active.revision);
        this.emitRemote("conflict");
        return;
      }
      throw e;
    }
  }

  private adoptState(state: ActiveState, revision: number): void {
    this.cache.state = state;
    this.cache.stateRevision = revision;
    this.cache.stateDirty = false;
    this.stateJson = JSON.stringify(state);
    this.writeCache();
  }

  private async pull(): Promise<void> {
    const res = await this.api.fetchJson<SyncPullResponse>(`/v1/sync?since=${this.cache.cursor}`);
    let changed = false;
    if (res.cursor < this.cache.cursor) {
      // Server was reset (e.g. staging data wiped): drop what we think we know.
      this.cache.cursor = 0;
    }
    for (const item of res.workouts) {
      if (this.dirty.has(item.id) || this.deleted.has(item.id)) continue; // local edit pending
      if (item.deleted || !item.workout) {
        if (this.cache.workouts[item.id]) {
          delete this.cache.workouts[item.id];
          changed = true;
        }
      } else if (JSON.stringify(this.cache.workouts[item.id]) !== JSON.stringify(item.workout)) {
        this.cache.workouts[item.id] = item.workout;
        changed = true;
      }
    }
    if (res.active && res.active.revision > this.cache.stateRevision && !this.cache.stateDirty) {
      if (JSON.stringify(res.active.state) !== this.stateJson) changed = true;
      this.adoptState(res.active.state, res.active.revision);
    }
    this.cache.cursor = res.cursor;
    this.cache.pulledOnce = true;
    this.writeCache();
    if (changed) this.emitRemote("pull");
  }
}
